import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/presentation/components/common/Icon';
import { CoofitLogoHomeButton } from '@/presentation/components/common/CoofitLogoHomeButton';
import { LanguageSelector } from '@/presentation/components/common/LanguageSelector';
import { PeriodFreshnessHint } from '@/presentation/components/common/PeriodFreshnessHint';
import { PeriodSelect } from '@/presentation/components/common/PeriodSelect';
import { ThemeToggle } from '@/presentation/components/common/ThemeToggle';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import {
  type MenuHouseDefinition,
  firstVisibleHousePath,
  hasPanelMenuAccess,
  isMenuHouseActive,
  isMoreSectionPath,
  visibleMenuHouses,
} from '@/application/navigation/menuHouses';
import { GlobalPeriodProvider, useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { MobileBottomNav } from '@/presentation/layouts/MobileBottomNav';
import { NoPermissionsShell } from '@/presentation/layouts/NoPermissionsShell';

function MainShellGate() {
  const { hasPermission, isOwner, isSimulating } = usePermissions();
  if (!hasPanelMenuAccess(hasPermission, isOwner && !isSimulating)) {
    return <NoPermissionsShell />;
  }
  return (
    <GlobalPeriodProvider>
      <MainShellFrame />
    </GlobalPeriodProvider>
  );
}

/** Shell autenticado: el periodo global vive aquí (chip en header). */
export function MainShell() {
  return <MainShellGate />;
}

function MainShellFrame() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { hasPermission, isOwner, isSimulating } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { period, setPeriod, periodOptions } = useGlobalPeriod();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(isMoreSectionPath(location.pathname));
  const menuOwner = isOwner && !isSimulating;

  useEffect(() => {
    if (isMoreSectionPath(location.pathname)) {
      setMoreOpen(true);
    }
  }, [location.pathname]);

  /** Pegar `/` o `/?period=…`: si no ve Resumen, ir a la primera casa del menú. */
  useEffect(() => {
    if (location.pathname !== '/') return;
    const home = firstVisibleHousePath(hasPermission, menuOwner);
    if (!home || home === '/') return;
    navigate({ pathname: home, search: location.search }, { replace: true });
  }, [hasPermission, menuOwner, location.pathname, location.search, navigate]);

  const primaryItems = visibleMenuHouses(hasPermission, 'primary', menuOwner);
  const moreItems = visibleMenuHouses(hasPermission, 'more', menuOwner);

  const renderNavItem = (item: MenuHouseDefinition, forceExpanded = false) => {
    const showLabel = forceExpanded || !collapsed;
    const active = isMenuHouseActive(location.pathname, item);
    const label = t(item.labelKey);
    return (
      <button
        key={item.to}
        type="button"
        title={!showLabel ? label : undefined}
        onClick={() => {
          navigate(item.to);
          setMobileOpen(false);
        }}
        className={`panel-nav-item ${showLabel ? 'gap-3 px-3' : 'justify-center px-2'} ${
          active ? 'panel-nav-item--active' : ''
        }`}
      >
        <Icon path={item.iconPath} className="h-5 w-5 shrink-0" />
        {showLabel && <span className="truncate">{label}</span>}
      </button>
    );
  };

  /** Dos árboles distintos: no reutilizar el mismo nodo React en desktop + drawer. */
  const renderSidebar = (opts: { forceExpanded?: boolean }) => {
    const forceExpanded = Boolean(opts.forceExpanded);
    const showMoreToggle = forceExpanded || !collapsed;
    const showMoreItems = forceExpanded || collapsed || moreOpen;
    return (
      <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto px-2 py-4">
        <div className="space-y-1">{primaryItems.map((item) => renderNavItem(item, forceExpanded))}</div>

        {moreItems.length > 0 && (
          <div>
            {showMoreToggle && (
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="panel-nav-section-label"
              >
                <span>{t('menu.sections.more')}</span>
                <span>{moreOpen ? '−' : '+'}</span>
              </button>
            )}
            {showMoreItems && (
              <div className="space-y-1">{moreItems.map((item) => renderNavItem(item, forceExpanded))}</div>
            )}
          </div>
        )}
      </nav>
    );
  };

  return (
    <div className="panel-shell-root has-mobile-bottom-nav relative h-full overflow-hidden">
      <aside
        className={`panel-shell-aside absolute inset-y-0 left-0 z-30 hidden flex-col transition-[width] duration-300 md:flex ${collapsed ? 'w-14' : 'w-64'}`}
      >
        <div
          className={`panel-shell-chrome-border-b flex h-16 shrink-0 items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-4'}`}
        >
          {!collapsed && (
            <CoofitLogoHomeButton className="h-7 w-auto object-contain" />
          )}
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="panel-shell-icon-btn h-9 w-9"
            aria-label="Toggle sidebar"
          >
            <Icon path="M4 6h16M4 12h16M4 18h16" />
          </button>
        </div>
        {renderSidebar({})}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="panel-shell-aside-solid panel-shell-drawer relative z-10 flex h-full w-64 flex-col overflow-visible">
            <div className="panel-shell-chrome panel-shell-chrome-border-b panel-shell-header flex shrink-0 items-center justify-between">
              <CoofitLogoHomeButton className="h-7 w-auto object-contain" />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="panel-touch panel-shell-icon-btn rounded-xl"
                aria-label="Close menu"
              >
                X
              </button>
            </div>
            {renderSidebar({ forceExpanded: true })}
            {/* Idioma y tema viven acá en mobile: en el header de 375px le robaban
             * el ancho al chip de periodo, que es el control que se usa. */}
            <div className="panel-shell-chrome-border-t relative z-20 flex shrink-0 items-center justify-between gap-2 overflow-visible px-4 py-3">
              <span className="panel-nav-section-label mb-0 px-0 py-0">
                {t('menu.sections.preferences')}
              </span>
              <div className="flex items-center gap-2">
                <LanguageSelector menuPlacement="up" />
                <ThemeToggle />
              </div>
            </div>
          </aside>
        </div>
      )}

      <div
        className={`flex h-full min-h-0 flex-col transition-[padding] duration-300 ${collapsed ? 'md:pl-14' : 'md:pl-64'}`}
      >
        <header className="panel-shell-chrome panel-shell-chrome-border-b panel-shell-header z-20 flex shrink-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="panel-touch panel-shell-icon-btn shrink-0 rounded-xl md:hidden"
              aria-label="Open menu"
            >
              <Icon path="M4 6h16M4 12h16M4 18h16" />
            </button>
            <CoofitLogoHomeButton className="h-6 w-auto shrink-0 object-contain md:hidden" />
            <PeriodSelect value={period} options={periodOptions} onChange={setPeriod} />
            <PeriodFreshnessHint placement="shell" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="panel-shell-user-chip">
              <span>{user?.username}</span>
            </div>
            <button
              type="button"
              onClick={logout}
              className="panel-shell-action-btn"
            >
              {t('auth.logout')}
            </button>
            {/* En mobile se ofrecen dentro del drawer (ver footer del aside). */}
            <div className="hidden items-center gap-2 md:flex">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </div>
        </header>

        <main className="panel-shell-main panel-shell-main-bg flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>

      <MobileBottomNav
        pathname={location.pathname}
        hasPermission={hasPermission}
        isOwner={menuOwner}
        onNavigate={(to) => {
          navigate(to);
          setMobileOpen(false);
        }}
        onOpenMore={() => {
          setMoreOpen(true);
          setMobileOpen(true);
        }}
      />
    </div>
  );
}
